import styled from "styled-components";
import {
  formatCurrency,
  formatCurrencyWithDecimals,
  formatDate,
  formatPercentage,
} from "../../formatters";
import { ReactElement } from "react";
import React from "react";

export type DataType =
  | "default"
  | "currency"
  | "currency-nillable"
  | "currency-with-decimals"
  | "percentage"
  | "date"
  | "boolean";
export interface Column<T = any> {
  header: string | React.ReactElement;
  accessor: keyof T | ((row: T) => any[] | string | number | null);
  align?: "left" | "center" | "right";
  style?: string;
  className?: string;
  highlight?: boolean;
  dataType?: DataType;
  highlightColumn?: boolean;
  filterNullAnd0?: boolean;
  violation?: (value: any) => boolean;
}

export interface StyleTableCellProps {
  extraStyle?: string;
  className?: string;
  align?: "left" | "center" | "right";
  highlight?: boolean;
  compact?: boolean;
  violation?: boolean;
  isFirstRow?: boolean;
  isLastRow?: boolean;
  isFirstCol?: boolean;
  isLastCol?: boolean;
}

export interface StyleTableProps {
  extraStyle?: string;
  compact?: boolean;
  className?: string;
  rotate?: boolean;
  regularStyles?: boolean;
}

export interface StyleTableRow {
  highlight?: boolean;
  index: number;
  compact?: boolean;
  className?: string;
  noBackground?: boolean;
  regularStyles?: boolean;
}

export const TableCell = styled.td.attrs<StyleTableCellProps>((props) => ({
  className: props.className || "",
}))<StyleTableCellProps>(
  ({
    theme,
    align = "left",
    extraStyle = ``,
    highlight = false,
    compact = false,
    violation = false,
    isFirstRow = false,
    isLastRow = false,
    isFirstCol = false,
    isLastCol = false,
  }) => `
  ${compact ? `padding: 3px; padding-left: 8px;` : `padding: ${theme.ptom(8)};`}
  text-align: ${align};
  position: relative;
  border-width: 0px;
  ${violation ? `color: red; font-weight: bold;` : ""}
  ${highlight ? `background-color: ${theme.colors.chartBlueLight};` : ""}
  border-radius: 0;
  ${
    isFirstRow && isFirstCol ? `border-top-left-radius: ${theme.ptom(12)};` : ""
  }
  ${
    isFirstRow && isLastCol ? `border-top-right-radius: ${theme.ptom(12)};` : ""
  }
  ${
    isLastRow && isFirstCol
      ? `border-bottom-left-radius: ${theme.ptom(12)};`
      : ""
  }
  ${
    isLastRow && isLastCol
      ? `border-bottom-right-radius: ${theme.ptom(12)};`
      : ""
  }
  ${extraStyle}
`
);

export const StyledTable = styled.table
  .withConfig({
    shouldForwardProp: (prop) =>
      !["rotate", "regularStyles", "extraStyle", "compact"].includes(prop),
  })
  .attrs<StyleTableProps>((props) => ({
    className: props.className || "",
  }))<StyleTableProps>(
  ({
    theme,
    extraStyle = ``,
    compact = false,
    rotate = false,
    regularStyles = false,
  }) => `
  color: ${theme.fg.title};
  width: 100%;
  font-size: ${theme.ptom(14)};
  text-align: left;
  border-spacing: ${rotate && !regularStyles ? "2px" : "0px"};
  border-width: 0px;
  table-layout: fixed;
  page-break-inside: avoid;
  &.regular-table {
    thead {
      th:first-child {
        text-align: left;
      }
    }
    tbody {
      td:first-child {
        text-align: left;
      }
    }
  }
  ${extraStyle}
`
);

export const TableHeader = styled.thead(
  ({ theme }) => `
  font-weight: 800;
  font-size: ${theme.ptom(7)};
  border-width: 0px;
  position: relative;
  z-index: 2;
`
);

export const TableHeaderCell = styled.th<
  Pick<StyleTableCellProps, "align" | "compact" | "extraStyle"> & {
    rotated?: boolean;
  }
>(
  ({
    theme,
    align = "left",
    rotated = false,
    compact = false,
    extraStyle = "",
  }) => `
  ${compact ? `padding: 3px; padding-left: 8px;` : `padding: ${theme.ptom(8)};`}
  font-size: ${compact ? theme.ptom(14) : theme.ptom(14)};
  font-weight: 800;
  text-align: ${align};
  border-width: 0px;
  position: relative;
  z-index: 2;
  ${
    rotated
      ? `
    white-space: nowrap;
    vertical-align: bottom;
    padding-bottom: ${compact ? theme.ptom(30) : theme.ptom(40)};
    > div {
     margin-left:20px !important;
      transform: rotate(-45deg);
      transform-origin: left bottom;
      position: absolute;
      bottom: ${compact ? theme.ptom(5) : theme.ptom(10)};
      left: ${compact ? theme.ptom(4) : theme.ptom(8)};
      width: max-content;
    }
  `
      : ""
  }
  &:first-child {
    margin-left: 8px;
  }
  ${extraStyle}
`
);

export const TableRow = styled.tr<StyleTableRow>(
  ({
    theme,
    index,
    highlight = false,
    compact = false,
    className,
    noBackground = false,
    regularStyles = false,
  }) => `
  height: ${compact ? "22px" : "26px"};
  background-color: ${
    regularStyles
      ? index % 2 === 0
        ? "#F3F6F8"
        : "#ffffff"
      : noBackground
      ? "transparent"
      : index % 2 === 0
      ? "#F3F6F8"
      : "#ffffff"
  };
  position: relative;
  
  z-index: 1;
`
);

export const TableFooterCell = styled.td.attrs<StyleTableCellProps>(
  (props) => ({
    className: props.className || "",
  })
)<StyleTableCellProps>(
  ({ theme, align = "left", extraStyle = ``, compact = false }) => `
  padding: ${compact ? theme.ptom(4) : theme.ptom(8)};
  font-weight: 800;
  text-align: ${align};
  ${extraStyle}
`
);

export const formatValue = (
  value: any,
  dataType?: DataType
): string | ReactElement => {
  // Handle {value, isOk} objects that might have slipped through
  if (value && typeof value === "object" && "value" in value && "isOk" in value) {
    return formatValue(value.value, dataType);
  }
  
  // If value is a React element, return it as is
  if (React.isValidElement(value)) {
    return value;
  }

  if (dataType === "currency-nillable") {
    return formatCurrency(value, "-");
  }

  if (dataType === "currency-with-decimals") {
    return formatCurrencyWithDecimals(value);
  }

  if (dataType === "currency") {
    return formatCurrency(value);
  }

  if (dataType === "percentage") {
    if (value === null || value === undefined) return "";
    return formatPercentage(value);
  }

  if (dataType === "date") {
    if (value === null || value === undefined) return "";
    return formatDate(value);
  }

  if (value === null || value === undefined) return "";
  return String(value);
};

// Helper function to get nested values (navigation properties like: overwaarde.specificatie.bedrag)
export const getNestedValue = (obj: any, path: string | keyof any): any => {
  // If path is not a string (e.g. a direct number/symbol key) or is an empty string, access directly.
  if (typeof path !== "string" || !path) {
    return obj && obj[path as keyof any];
  }
  // If path is a string, try to access nested properties.
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};
